import { Fragment, JSX } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { Review, ReviewSummary } from '../types/review'

interface ReviewsModalProps {
  isOpen: boolean
  onClose: () => void
  reviews: Review[]
  reviewSummary: ReviewSummary | null
  renderStars: (rating: number) => JSX.Element
  renderReviewCard: (review: Review) => JSX.Element
}

export default function ReviewsModal({
  isOpen,
  onClose,
  reviews,
  reviewSummary,
  renderStars,
  renderReviewCard
}: ReviewsModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[560px] overflow-hidden rounded-2xl relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)', maxHeight: '80vh' }}
              >
                {/* Close button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded-full p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors z-10"
                  onClick={onClose}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                {/* Header */}
                <div className="p-6 md:p-8 pb-0 md:pb-0">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-full grid place-items-center mb-5"
                    style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                    </svg>
                  </div>

                  <h3 className="text-[22px] font-medium tracking-tight">All <span className="font-serif italic font-normal">reviews</span></h3>

                  {reviewSummary && (
                    <div className="flex items-center gap-2.5 mt-2">
                      {renderStars(Math.round(reviewSummary.averageRating))}
                      <span className="text-[12.5px] text-white/50">
                        {reviewSummary.averageRating.toFixed(1)} average · {reviewSummary.totalReviews} review{reviewSummary.totalReviews !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {/* Rating distribution */}
                  {reviewSummary && reviewSummary.totalReviews > 0 && (
                    <div className="rounded-xl p-4 mt-5 space-y-2"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      {[5, 4, 3, 2, 1].map((r) => {
                        const count = reviewSummary.ratingDistribution[r as keyof typeof reviewSummary.ratingDistribution]
                        const pct = reviewSummary.totalReviews > 0 ? (count / reviewSummary.totalReviews) * 100 : 0
                        return (
                          <div key={r} className="flex items-center gap-3">
                            <span className="text-[11px] text-white/40 w-5 text-right">{r}</span>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-amber-400 shrink-0">
                              <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                            </svg>
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(0 0% 15%)' }}>
                              <div
                                className="h-full rounded-full bg-amber-400/80 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[11px] text-white/30 w-5">{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Reviews list */}
                <div className="overflow-y-auto px-6 md:px-8 pt-5 pb-6 md:pb-8" style={{ maxHeight: 'calc(80vh - 320px)' }}>
                  {reviews.length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="mx-auto w-10 h-10 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                      </svg>
                      <h4 className="mt-3 text-sm font-medium text-white">No reviews yet</h4>
                      <p className="mt-1 text-[12.5px] text-white/40">
                        Reviews will appear here after completed sessions.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-[10px] tracking-[2px] uppercase text-white/40 mb-4">
                        {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                      </div>
                      <div className="space-y-4">
                        {reviews.map((review, index) => (
                          <div key={review.id}
                            className={index !== reviews.length - 1 ? 'pb-4' : ''}
                            style={index !== reviews.length - 1 ? { borderBottom: '1px solid hsl(0 0% 15%)' } : undefined}>
                            {renderReviewCard(review)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 md:px-8 pb-6 md:pb-8">
                  <button
                    onClick={onClose}
                    className="btn btn-outline w-full justify-center !py-3"
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
